/**
 * Copyright Mántum All Rights Reserved.
 */
import Internal from '../internal/select';
import ReactSelect from 'react-select/async-creatable';
import debug from 'debug';
import request from '@mantum/kanela-request';
import { forwardRef, useRef, useState } from 'react';
import { source, helper, accent, validator } from '@mantum/kanela';

import type {
    DataSource,
    Detail,
    Filter,
    Group,
    Locale,
    Option,
    Options,
    Result,
    Validation
} from '@mantum/kanela';

const info = debug('kanela:select');

type PropTypes = {
    advanced?: {
        allowAdding?: boolean;
        characters?: number;
        delay?: number;
        entity?: string;
        filter?: Filter;
        group?: Group;
        multiple?: boolean;
        options?: Option<string>;
        result?: Result;
    };
    dataSource?: unknown[] | DataSource;
    disabled?: boolean;
    locale?: Locale;
    error?: boolean;
    instanceId?: number | string;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: Option<unknown> | unknown) => void;
    optional?: boolean;
    placeholder?: string;
    testid?: string;
    open?: boolean;
    validation?: Validation;
    value?: Option<unknown>;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function Autocomplete(props, ref) {
        // prettier-ignore
        const {
            advanced = {}
        } = props;

        // prettier-ignore
        const { 
            characters = 3,
            delay = 500,
            multiple = false
        } = advanced;

        const timeoutRef = useRef<NodeJS.Timeout>();
        const dataSourceRef = useRef(props.dataSource);

        const [loading, setLoading] = useState(false);
        const [original, setOriginal] = useState<unknown[]>([]);

        const onLoadOptions = (dataSource?: DataSource) => {
            dataSourceRef.current = dataSource ? dataSource : props.dataSource;
            info(
                '%s: change new datasource %o',
                props.name,
                dataSourceRef.current
            );
        };

        const isValid = (value: string) => {
            if (value.length < characters) {
                return false;
            }
            return true;
        };

        const toSelect = (values: unknown[]): Options<unknown> => {
            const label = props.advanced?.options?.label
                .split('.')
                .filter((it) => it !== '') ?? ['label'];

            const value = props.advanced?.options?.value
                .split('.')
                .filter((it) => it !== '') ?? ['value'];

            return helper.select.convert(values, label, value);
        };

        const getLoadOptions = (inputValue: string) => {
            return new Promise((resolve, reject) => {
                const value = inputValue.trim();
                if (!isValid(value)) {
                    resolve([]);
                    return;
                }

                if (timeoutRef.current !== undefined) {
                    clearTimeout(timeoutRef.current);
                }

                timeoutRef.current = setTimeout(() => {
                    if (!dataSourceRef.current) {
                        return;
                    }

                    if (!validator.isDataSource(dataSourceRef.current)) {
                        return;
                    }

                    setLoading(true);
                    const dataSource = dataSourceRef.current as DataSource;

                    // prettier-ignore
                    const {
                        parameter = {}
                    } = dataSource

                    const parameters = {
                        ...parameter
                    };

                    Object.keys(parameters).forEach((it) => {
                        const key = parameters[it];
                        if (typeof key === 'string') {
                            parameters[it] = key.replace('{value}', value);
                        }
                    });

                    const url = dataSource.url
                        .replace('{value}', value)
                        .replace('{current}', value);

                    info('search: %s', url);
                    const response = request.fetch(url, {
                        method: dataSource.method,
                        body: parameters,
                        headers: dataSource.headers
                    });

                    response.then((values: unknown[]) => {
                        if (!values) {
                            setLoading(false);
                            return;
                        }

                        const results = source.extract<unknown[]>(
                            values,
                            dataSource.namespace
                        );

                        setLoading(false);
                        if (!Array.isArray(results)) {
                            reject(
                                new Error('The response value is not array')
                            );
                            return;
                        }

                        setLoading(false);
                        setOriginal(results);

                        const items = toSelect(results);
                        resolve(items);
                    });

                    response.catch((error: unknown) => {
                        setLoading(false);
                        reject(error);
                    });
                }, delay);
            });
        };

        const getLocalLoadOptions = (inputValue: string) => {
            return new Promise((resolve, reject) => {
                const value = inputValue.trim();
                if (!isValid(value)) {
                    resolve([]);
                    return;
                }

                if (!Array.isArray(dataSourceRef.current)) {
                    reject(new Error('The data source is not array'));
                    return;
                }

                setOriginal(dataSourceRef.current);
                resolve(
                    toSelect(dataSourceRef.current).filter((row) =>
                        accent(row.label).includes(accent(value))
                    )
                );
            });
        };

        const loadOptions = (inputValue: string) => {
            return !Array.isArray(dataSourceRef.current)
                ? getLoadOptions(inputValue)
                : getLocalLoadOptions(inputValue);
        };

        return (
            <Internal
                {...props}
                cacheOptions={false}
                dataSource={[]}
                loadOptions={loadOptions}
                loading={loading}
                multiple={multiple}
                onLoadOptions={onLoadOptions}
                original={original}
                ref={ref}
                render={ReactSelect}
            />
        );
    }
);
